import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { http } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Sparkles, Star, StarOff, Trash2, Play, Search } from "lucide-react";

/** Deals console — approve / schedule / boost / retire Deal-of-the-Day cards. */
export default function CmsDeals() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [kind, setKind] = useState("");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await http.get("/deals", { params: { limit: 50, kind: kind || undefined, q: q || undefined } });
      setItems(r.data?.items || []);
      setTotal(r.data?.total || 0);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load deals");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [kind]);

  const republish = async (slug) => {
    try {
      // Existing endpoint bumps the deal's `starts_at` and `ends_at` window.
      const now = new Date();
      const in7d = new Date(now.getTime() + 7 * 86400_000);
      await http.post(`/deals/${encodeURIComponent(slug)}/republish`, {
        starts_at: now.toISOString(),
        ends_at: in7d.toISOString(),
      });
      toast.success("Deal republished for 7 days");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Republish failed");
    }
  };

  const remove = async (slug) => {
    if (!window.confirm("Retire this deal? It will disappear from the public feed.")) return;
    try {
      await http.delete(`/deals/${encodeURIComponent(slug)}`);
      toast.success("Deal retired");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Retire failed");
    }
  };

  return (
    <div className="max-w-6xl" data-testid="cms-deals">
      <Link to="/admin/cms" className="mb-3 inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700">
        <ArrowLeft className="h-3.5 w-3.5" /> Back to CMS home
      </Link>
      <h1 className="text-2xl font-semibold text-slate-900">Deals</h1>
      <p className="mt-1 text-sm text-slate-500">Approve, schedule and boost Deal-of-the-Day cards — {total} in the feed.</p>

      <div className="mt-4 flex flex-wrap items-center gap-2">
        {["", "offer", "coupon", "surplus"].map((k) => (
          <button
            key={k || "all"}
            data-testid={`cms-deals-filter-${k || "all"}`}
            onClick={() => setKind(k)}
            className={`rounded-full border px-3 py-1 text-xs ${kind === k ? "border-emerald-300 bg-emerald-50 text-emerald-700" : "border-slate-200 bg-white text-slate-600"}`}
          >
            {k || "all"}
          </button>
        ))}
        <div className="relative flex-1 max-w-sm">
          <Search className="pointer-events-none absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} placeholder="Search title, vendor…" className="pl-8" data-testid="cms-deals-search" />
        </div>
        <Button variant="outline" onClick={load} disabled={loading}>{loading ? "Loading…" : "Search"}</Button>
      </div>

      <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
        {items.map((d) => (
          <DealCard key={d.slug} d={d} onRepublish={republish} onRemove={remove} onChanged={load} />
        ))}
        {items.length === 0 && !loading && (
          <div className="col-span-full rounded-xl border border-dashed border-slate-300 bg-white p-8 text-center text-sm text-slate-500">
            No deals match this filter.
          </div>
        )}
      </div>
    </div>
  );
}

function DealCard({ d, onRepublish, onRemove, onChanged }) {
  const [featured, setFeatured] = useState(!!d.featured);
  useEffect(() => setFeatured(!!d.featured), [d.featured]);
  const toggleBoost = async () => {
    // Piggy-back on the food-categories featured update trick — deals use the
    // same admin_messages plumbing but we go through the deal endpoint if it
    // exists; otherwise fall back to a PATCH via /deals/{slug}.
    try {
      await http.patch(`/deals/${encodeURIComponent(d.slug)}`, { featured: !featured });
      setFeatured(!featured);
      toast.success(!featured ? "Boosted to top of feed" : "Boost removed");
      onChanged?.();
    } catch (e) {
      // Non-fatal: some deployments don't expose a PATCH on /deals.
      toast.message("Boost requires a deployment update — coming soon");
    }
  };
  const price = d.deal_price != null ? `$${d.deal_price}` : (d.discount_pct != null ? `${d.discount_pct}% off` : (d.discount_amount != null ? `$${d.discount_amount} off` : "—"));
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid={`cms-deal-${d.slug}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 text-sm font-semibold text-slate-900">
            <Sparkles className="h-4 w-4 text-emerald-500" /> {d.title}
          </div>
          <div className="mt-0.5 truncate text-xs text-slate-500">{d.vendor_name || d.vendor_slug || "—"}</div>
          <div className="mt-1 flex flex-wrap gap-2 text-[11px] text-slate-500">
            <span>{d.kind}{d.deal_type ? ` · ${d.deal_type}` : ""}</span>
            <span>·</span>
            <span>{d.city || d.state || d.country || "—"}</span>
            {d.ends_at && (<><span>·</span><span>ends {String(d.ends_at).slice(0, 10)}</span></>)}
          </div>
        </div>
        <span className="shrink-0 rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-medium text-emerald-700">{price}</span>
      </div>
      <div className="mt-3 flex flex-wrap gap-1.5">
        <Button size="sm" onClick={() => onRepublish(d.slug)} data-testid={`cms-deal-republish-${d.slug}`} className="h-7 bg-emerald-600 text-white hover:bg-emerald-700">
          <Play className="mr-1 h-3 w-3" /> Republish 7d
        </Button>
        <Button size="sm" variant="outline" onClick={toggleBoost} className="h-7" data-testid={`cms-deal-boost-${d.slug}`}>
          {featured ? <StarOff className="mr-1 h-3 w-3" /> : <Star className="mr-1 h-3 w-3" />} {featured ? "Unboost" : "Boost"}
        </Button>
        <Button size="sm" variant="outline" onClick={() => onRemove(d.slug)} className="h-7 border-rose-200 text-rose-600 hover:bg-rose-50">
          <Trash2 className="mr-1 h-3 w-3" /> Retire
        </Button>
      </div>
    </div>
  );
}
